import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserRoleService } from '../auth/user-role.service';
import { dashboardEntryRoute } from '@app/domain/models/dashboard-lines.model';

/** Qué exige una ruta (`route.data.requiresGestor`). */
export type RequiredGestor = 'dashboard' | 'spei' | 'tpv' | 'payments';

/**
 * Restringe el acceso a las rutas según las líneas de negocio del usuario, con la
 * regla ÚNICA del dominio (`domain/models/dashboard-lines.model.ts`, feature 108):
 *
 *  - `'dashboard'` → el host `/dashboard`: pasa con 2+ líneas (vista Todo) o con un
 *     gestor SPEI estricto (vista Envíos) (R32).
 *  - `'spei'`  → exige gestor SPEI ESTRICTO (`service` SPEI o vacío): un gestor de
 *     Cards o de PAYMENTS ya no abre las pantallas de SPEI, que banca le
 *     rechazaría (R33).
 *  - `'tpv'`   → exige gestor TPV.
 *  - `'payments'` → la vista "Pasarela de Pagos" (merge de dev, feature 60 de dev):
 *     exige E-commerce USABLE (`canUseEcommerce()`: gestor PAYMENTS y el módulo
 *     encendido en el ambiente).
 *
 * Toda redirección va a la entrada de dashboard del usuario (`dashboardEntryRoute`;
 * solo-PAYMENTS ⇒ `/e-commerce/dashboard`), nunca a `/login` para una sesión válida
 * (R34). Si esa entrada fuera la misma ruta pedida (no debería: la regla es
 * coherente), cae a `/products` para no hacer bucle.
 */
export const gestorGuard: CanActivateFn = (route, state) => {
  const userRole = inject(UserRoleService);
  const router = inject(Router);

  const requires = route.data?.['requiresGestor'] as RequiredGestor | undefined;
  if (!requires) return true;

  const lines = userRole.getBusinessLines();
  const hasSpei = userRole.hasSpeiService();

  const allowed =
    requires === 'dashboard' ? lines.length >= 2 || hasSpei
    : requires === 'spei' ? hasSpei
    : requires === 'payments' ? userRole.canUseEcommerce()
    : userRole.hasTpvAccess();
  if (allowed) return true;

  const entry = dashboardEntryRoute(lines);
  const requested = (state?.url ?? '').split(/[?#]/)[0];
  const loops = requested === entry || requested.startsWith(`${entry}/`);
  return router.createUrlTree([loops ? '/products' : entry]);
};
